/**
 * Offline outbox: field writes that could not reach Supabase, persisted in
 * localStorage and replayed when the connection comes back.
 *
 * Scope is deliberately narrow — single-column updates on work_order_tasks
 * (crew / PM / materials / finance notes, SOW). One entry per row+field, last
 * write wins, so a crew member can keep editing a note in a basement and the
 * final text is what syncs. Entries survive reloads and app restarts.
 *
 * Flushing is sequential and stops at the first network failure so the
 * remaining entries wait for the next trigger (see useOutboxSync). A
 * non-network, non-auth failure (RLS, bad column) drops the entry and tells
 * the user, so one poisoned write can never block the rest of the queue.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase/client'
import { useAppStore } from '@/stores/app-store'
import { isAuthError, isNetworkError, isOnline, withTimeout } from './network'

export type OutboxTable = 'work_order_tasks'

export interface OutboxAfter {
  /** Edge function to invoke once the row update lands (e.g. a Monday push). */
  fn: string
  body: Record<string, unknown>
}

export interface OutboxEntry {
  /** `${table}:${rowId}:${field}` — one live entry per field. */
  id: string
  table: OutboxTable
  rowId: string
  field: string
  value: unknown
  /** Extra columns written alongside the field (attribution stamps etc). */
  patch?: Record<string, unknown>
  orgId: string | null
  queuedAt: number
  /** Unique per enqueue, so a value re-queued while its predecessor is in
   *  flight is never mistaken for the one that just landed. */
  rev: string
  attempts: number
  lastError?: string
  after?: OutboxAfter
}

export type EnqueueInput = Omit<OutboxEntry, 'id' | 'queuedAt' | 'rev' | 'attempts' | 'lastError'>

const newRev = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

export function outboxId(table: OutboxTable, rowId: string, field: string): string {
  return `${table}:${rowId}:${field}`
}

interface OutboxState {
  entries: OutboxEntry[]
  syncing: boolean
  online: boolean
  lastSyncedAt: number | null
  enqueue: (input: EnqueueInput) => void
  remove: (id: string) => void
  setOnline: (online: boolean) => void
  /** Replay queued writes. Resolves with how many landed and how many remain. */
  flush: () => Promise<{ sent: number; remaining: number }>
}

const SEND_TIMEOUT_MS = 15_000

type SendResult = 'ok' | 'retry' | 'drop'

async function sendEntry(entry: OutboxEntry, refreshed = false): Promise<{ result: SendResult; error?: string }> {
  try {
    let q = supabase
      .from(entry.table)
      .update({ ...(entry.patch ?? {}), [entry.field]: entry.value, updated_at: new Date().toISOString() } as never)
      .eq('id', entry.rowId)
    if (entry.orgId) q = q.eq('organization_id', entry.orgId)
    // PostgREST builders are thenables, not Promises — wrap before racing.
    const { error } = await withTimeout(Promise.resolve(q).then((r) => r), SEND_TIMEOUT_MS, 'outbox update')
    if (!error) return { result: 'ok' }
    if (isNetworkError(error)) return { result: 'retry', error: error.message }
    if (isAuthError(error)) {
      // Expired token: refresh once and retry. If auth is still failing the
      // entry is kept, never dropped — the next trigger (or next sign-in on
      // this device) replays it.
      if (!refreshed) {
        const { error: refreshErr } = await supabase.auth.refreshSession()
        if (!refreshErr) return sendEntry(entry, true)
        return { result: 'retry', error: refreshErr.message }
      }
      return { result: 'retry', error: error.message }
    }
    return { result: 'drop', error: error.message }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    return { result: isNetworkError(e) ? 'retry' : 'drop', error: msg }
  }
}

function runAfter(after: OutboxAfter | undefined) {
  if (!after) return
  void supabase.functions.invoke(after.fn, { body: after.body })
    .then((r) => { if (r.error) console.warn(`outbox after ${after.fn} failed (non-blocking):`, r.error) })
    .catch((e) => console.warn(`outbox after ${after.fn} threw (non-blocking):`, e))
}

export const useOutboxStore = create<OutboxState>()(
  persist(
    (set, get) => ({
      entries: [],
      syncing: false,
      online: isOnline(),
      lastSyncedAt: null,

      enqueue: (input) => {
        const id = outboxId(input.table, input.rowId, input.field)
        const entry: OutboxEntry = { ...input, id, queuedAt: Date.now(), rev: newRev(), attempts: 0 }
        set((s) => {
          const others = s.entries.filter((e) => e.id !== id)
          return { entries: [...others, entry] }
        })
      },

      remove: (id) => set((s) => ({ entries: s.entries.filter((e) => e.id !== id) })),

      setOnline: (online) => set({ online }),

      flush: async () => {
        if (get().syncing || get().entries.length === 0 || !isOnline()) {
          return { sent: 0, remaining: get().entries.length }
        }
        set({ syncing: true })
        let sent = 0
        try {
          // Keep passing until a pass sends nothing (a value re-queued while
          // its predecessor was in flight gets picked up on the next pass).
          let progressed = true
          let stop = false
          while (progressed && !stop) {
            progressed = false
            for (const snapshot of [...get().entries]) {
              const live = get().entries.find((e) => e.id === snapshot.id)
              if (!live) continue
              const { result, error } = await sendEntry(live)
              if (result === 'ok') {
                // Only clear if nobody re-queued a newer value meanwhile.
                set((s) => ({
                  entries: s.entries.filter((e) => !(e.id === live.id && e.rev === live.rev)),
                  lastSyncedAt: Date.now(),
                }))
                runAfter(live.after)
                sent++
                progressed = true
              } else if (result === 'retry') {
                set((s) => ({
                  entries: s.entries.map((e) => e.id === live.id ? { ...e, attempts: e.attempts + 1, lastError: error } : e),
                }))
                stop = true
                break
              } else {
                set((s) => ({ entries: s.entries.filter((e) => e.id !== live.id) }))
                useAppStore.getState().addToast({
                  type: 'error',
                  title: 'A saved change could not be uploaded',
                  message: `${live.field.replace(/_/g, ' ')}: ${error ?? 'unknown error'}`,
                  duration: 8000,
                })
              }
            }
          }
        } finally {
          set({ syncing: false })
        }
        return { sent, remaining: get().entries.length }
      },
    }),
    {
      name: 'opsyri-outbox',
      partialize: (s) => ({ entries: s.entries }),
    },
  ),
)

/**
 * Selector helper: the queued entry for a field, if any. Returns the stored
 * entry object itself (a stable reference until it is replaced) — zustand v5
 * requires selector results to be referentially stable between renders.
 */
export function selectQueuedValue(id: string) {
  return (s: OutboxState): OutboxEntry | null => s.entries.find((x) => x.id === id) ?? null
}
